import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseService } from '@projects/shared-lib/src/public-api';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { tap, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ESTABLISH_PROJECT_TOOLS } from '@projects/shared-lib/src/lib/core/polling-constants';
import { PollingService } from '@projects/shared-lib/src/lib/services/polling.service';
import { IFileData } from '../core/utility/constants';

@Injectable({
  providedIn: 'root'
})
export class ToolsAcceleratorsControlsService {

  public toolData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public featureData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public clickData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public moduleData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public msteamsDashboardData: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private packageContentMicroservice: string = environment.API_MICROSERVICE_URL.PACKAGE_CONTENT;
  private projectMicroservice: string = environment.API_MICROSERVICE_URL.PROJECT;
  private adminMicroServiceUrl: string = environment.API_MICROSERVICE_URL.ADMIN;
  private userMicroserviceUrl: string;
  private dfteToolsSubject = new BehaviorSubject<any>(null);
  private dfteToolsSubjectGenai  = new BehaviorSubject<any>(null);
  dfteTools$ = this.dfteToolsSubject.asObservable();
  dfteToolsGenai$ = this.dfteToolsSubjectGenai.asObservable();
  private refreshGenAIToolsSubject = new Subject<void>();
  public refreshGenAITools$ = this.refreshGenAIToolsSubject.asObservable();
  private refreshTraditionalToolsSubject = new Subject<void>();
  public refreshTraditionalTools$ = this.refreshTraditionalToolsSubject.asObservable();
  private changeRequestCache: Map<string, Observable<any>> = new Map();

  constructor(private http: HttpClient, private baseService: BaseService, private pollingService: PollingService) {
    this.userMicroserviceUrl = environment.API_MICROSERVICE_URL?.USER;
  }

  triggerGenAIToolsRefresh(): void {
    this.refreshGenAIToolsSubject.next();
  }

  triggerTraditionalToolsRefresh(): void {
    this.refreshTraditionalToolsSubject.next();
  }

  requestToolsInstance(projectId: string): Observable<any> {
    const exploreModeEndpoint = `/tools/dfteTools/explore`;
    const projectModeEndpoint = `/tools/dfteTools/${projectId}`;
    const url = projectId == '0' ? exploreModeEndpoint : projectModeEndpoint;
    const baseUrl = projectId == '0' ? this.adminMicroServiceUrl : this.projectMicroservice;

    this.dfteToolsSubject.next(null);
    this.dfteToolsSubjectGenai.next(null);

    return this.baseService.get(url, baseUrl).pipe(
      tap((data) => {
        this.dfteToolsSubjectGenai.next(data);
        this.dfteToolsSubject.next(data);
      }),
      shareReplay(1)
    );
  }

  isReleaseAIEnabled(projectId: string): Observable<any> {
    return this.baseService.get(`/project/release-ai-enabled/${projectId}`, this.projectMicroservice);
  }

  getAcceleratorAppsData(projectId): Observable<any> {
    const exploreModeEndpoint = '/tools/dfte/digitalaccelerators/explore/3';
    const projectModeEndpoint = `/tools/dfte/digitalaccelerators/${projectId}/3`;
    const url = projectId == '0' ? exploreModeEndpoint : projectModeEndpoint;
    const baseUrl =  projectId == '0' ? this.adminMicroServiceUrl : this.projectMicroservice;
    return this.baseService.get(url,baseUrl);
  }

  getDFTEUsersData(projectId, isSmartForceEstablish): Observable<any> {
    if (isSmartForceEstablish) {
      return this.baseService.get(
        `/tools/projectSmartforceUsers/${projectId}`, this.projectMicroservice
      );
    }
    else {
      return this.baseService.get(`/tools/projectDfteUsers/${projectId}`, this.projectMicroservice);
    }
  }

  getECUsersData(projectId,toolId?): Observable<any> {
    return this.baseService.get(`/tools/projectToolUsers/${projectId}/${toolId}`, this.projectMicroservice);
  }

  getToolUserRoleData(toolId): Observable<any> {
    return this.baseService.get(`/tools/user-roles/${toolId}`, this.projectMicroservice);
  }



  setDFTEManageUsers(projectId, data) {
      return this.baseService.post(
        `/tools/dfte/user-provisioning/${projectId}`,
        data, this.projectMicroservice
      );
  }

  setSmartforceManageUsers(projectId, data) {
      return this.baseService.post(
        `/tools/smartforce/user-provisioning/${projectId}`,
        data, this.projectMicroservice
      );
  }

  provisionECManageUsers(projectId, data) {
    return this.baseService.post(`/tools/user-provisioning/${projectId}`,data, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  /**
   * Retrieves the details of a tool from the marketplace.
   *
   * @param {number | string} appId - The ID of the amplifier. Can be a number, string.
   * @param {number | string} languageId - The ID of the language. Can be a number, string.
   * @param {number | null} packageId - The ID of the package. Can be a number or null.
   * @returns An observable containing the tool details.
   */
  getToolDetails = (
    appId: number | string,
    languageId: number | string,
    packageId: number | null
  ) => {
    return this.baseService.get(
      `/marketplace/tool-info?toolid=${appId}&languageId=${languageId}&packageId=${packageId}`,
      this.packageContentMicroservice
    );
  }

  setProvisionApp(projectId, data, isSmartForceEstablish) {
    // if (isSmartForceEstablish) {
    //   return this.baseService.post(
    //     `/tools/smartforce/project-provisioning/${projectId}`,
    //     data, this.projectMicroservice
    //   );
    // } else {
      return this.baseService.post(
        `/tools/dfte/projectProvisioning/${projectId}`,
        data, this.projectMicroservice
      );
    // }
  }

  setToolProvision(id, tool) {

    if(tool?.entityName) {
      return this.baseService.postWithRefreshToken(`${environment.API_MICROSERVICE_URL?.PROJECT}/tools/provision/${id}/${tool.entityName}`, JSON.parse(JSON.stringify(tool)));
    }
    return this.http.post<any>(`${environment.API_MICROSERVICE_URL?.PROJECT}/tools/provision/${id}/${tool[0].entityName}`, tool);
  }

  public sendToolData(data: any): void {
    this.toolData.next(data);
  }

  getToolData(): Observable<any> {
    return this.toolData.asObservable();
  }

  public sendFeatureData(data: any): void {
    this.featureData.next(data);
  }

  getFeatureData(): Observable<any> {
    return this.featureData.asObservable();
  }

  public sendValue(val: any): void {
    this.clickData.next(val);
  }
  public sendValueToDashboard(val: any): void {
    this.msteamsDashboardData.next(val);
  }

  getValueToDashboard(): Observable<any> {
    return this.msteamsDashboardData.asObservable();
  }


  getValue(): Observable<any> {
    return this.clickData.asObservable();
  }

  updateParams(projectId, feature, toolData) {
    return this.http.post<any>(`${environment.API_MICROSERVICE_URL.PROJECT}/tools/updateprovisiondetails/${projectId}/${feature}`, toolData);
  }

  addParams(toolId, projectId, body) {
    return this.http.put<any>(
      `${environment.API_MICROSERVICE_URL.PROJECT}/tools/updateparameters/${toolId}/${projectId}`,
      body
    );
  }

  updateSapCalmParameters(projectId: number, body: {
    sapProjectReferenceId: string;
    tokenUrl: string;
    apiBaseUrl: string;
    clientId: string;
    clientSecret: string;
  }, skipLoader = false) {
    const headers = skipLoader ? new HttpHeaders({ skip: 'true' }) : new HttpHeaders();
    return this.http.put<any>(
      `${environment.API_MICROSERVICE_URL.PROJECT}/tools/sap-calm/updateparameters/${projectId}`,
      body,
      { headers }
    );
  }

  getaccessToMSTeamTool(postData) {
    const exportToTeamsDurableUrl = `/api/process/start/JoinMSTeamsActivity?projectid=${postData?.projectId}&featureId=${postData?.featureId}`;
    const url = `${environment.API_BASE_URL}${exportToTeamsDurableUrl}`;
    this.http.post(url, {}).subscribe(
      (data: any) => {
        this.pollingService.startPolling(data.instanceId, ESTABLISH_PROJECT_TOOLS);
        this.pollingService.getPollingData().subscribe((res) => { });
      },
      (err) => {
      }
    );
  }

  public sendModuleData(data: any): void {
    this.moduleData.next(data);
  }

  getModuleData(): Observable<any> {
    return this.moduleData.asObservable();
  }

  getUserStorytypes(projectId) {
    return this.http.get<any>(`${environment.API_BASE_URL}/deliver/userStoryTypes/${projectId}`);
  }

  getChangeRequestStatus(projectId) {
    const cacheKey = `${projectId}`;
    if (!this.changeRequestCache.has(cacheKey)) {
      const request$ = this.baseService.get(`/psg/scope_change_request/details/${projectId}`, environment.API_MICROSERVICE_URL.PROJECT).pipe(
        shareReplay(1)
      );
      this.changeRequestCache.set(cacheKey, request$);
    }
    return this.changeRequestCache.get(cacheKey);
  }

  clearChangeRequestCache(projectId?: string) {
    if (projectId) {
      this.changeRequestCache.delete(projectId);
    } else {
      this.changeRequestCache.clear();
    }
  }

  getExportedUsersData(projectId): Observable<any> {
    return this.baseService.get(`/user/export/project-users?projectId=${projectId}`, this.userMicroserviceUrl);
  }

  startSapTaxonomySyncProcess(projectId) {
    return this.baseService.post(`/process/schedule/sap_calm_taxonomy_sync?projectid=${projectId}`, {}, environment.API_MICROSERVICE_URL.PROJECT);
  }

  provisionTestAIUsers(data) {
    return this.baseService.post(`/user/export/project-users`, data, this.userMicroserviceUrl);
  }

  getUserStorySettingsStatus(projectId){
    return this.baseService.get(`/tools/getReverseIntegrationDetails/${projectId}`,environment.API_MICROSERVICE_URL.PROJECT);
  }


  getTestAIProvisionInfo(toolId, projectId) {
    return this.baseService.get(`/tools/getTestAIProvisionInfo/${projectId}/${toolId}`, environment.API_MICROSERVICE_URL.PROJECT)
  }

  getToolsUpdates(toolId, projectId, toolName) {
    return this.baseService.get(`/tools/get-tool-updates/${projectId}/${toolId}/${toolName}`, environment.API_MICROSERVICE_URL.PROJECT)
  }

  saveUserStoriesSettings(settings){
    return this.baseService.post('/tools/update-reverse-integration-details',settings, environment.API_MICROSERVICE_URL.PROJECT);
  }

  syncUserStories(projectId, packageId, functionId, activityId, userroleid, payload){
    let syncUSUrl = `${environment.API_MICROSERVICE_URL.PROJECT}/process/schedule/user_stories_sync?projectid=${projectId}&packageid=${packageId}&functionid=${functionId}&activityid=${activityId}`;
    return this.http.post(syncUSUrl, payload)
  }

  getGenAITools(projectId, packageId, toolCategoryId, languageId){
    return this.baseService.get(`/project/genai-amplifiers/${projectId}/${packageId}/${toolCategoryId}?languageId=${languageId}`, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  saveProjectToolConsent(projectId, toolId, body?){
    return this.baseService.post(`/project/saveConsent/${projectId}/${toolId}`, body, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  saveGenAiTools(projectId, toolCategoryId, body){
    return this.baseService.post(`/project/genaitools/${projectId}/${toolCategoryId}`, body, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  sendGenAIEnabledEmail(projectId) {
    return this.baseService.post(`/project/sendGenAIAmplifiersEmail/${projectId}`, {}, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  saveOtherTools(projectId, body){
    return this.baseService.post(`/project/tool-provision/${projectId}`, body, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  getProjectInfo(projectId: any, userId) {
    return this.http.get(
      environment.API_MICROSERVICE_URL.PROJECT + '/project/' + projectId+`/1`);
  }

  getMsTeamsStatus(projectId) {
    return this.http.get(`${environment.API_MICROSERVICE_URL?.PROJECT}/tools/msteams-status/${projectId}`);
  }

  saveToolUpdatesHistory(body){
    return this.baseService.post(`/tools/save-tool-updates`, body, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  getdfteToolsMsTeamsStatus(projectId) {
    return this.http.get(`${environment.API_MICROSERVICE_URL?.PROJECT}/tools/dfteTools/${projectId}`);
  }

  getdfteTools(projectId) {
    return this.http.get(`${environment.API_MICROSERVICE_URL?.PROJECT}/tools/dfteTools/${projectId}`);
  }
  dfteToolsDataObv(data: any) {
    this.dfteToolsSubject.next(data);
  }

  //to fetch Gen-AI status of Test AI provision
  getTestAIProjectDetails(projectId: string) {
    return this.http.get(`${environment.TEST_AI_API}project/gen-ai-status/${projectId}`);
  }
  /**
   * VerifyWorkspaceId: Verifies the workspace ID for a given tool
   * by making an HTTP GET request.
   *
   * @param payload - An object containing the following properties:
   *   @param payload.toolId - The unique identifier of the tool.
   *   @param payload.workspaceId - The unique identifier of the workspace.
   *
   * @returns An observable of the HTTP GET request response.
   */
  verifyWorkspaceId = (payload: {
    toolId: string;
    workspaceId: string;
    projectId: string;
  }) => {
    const { toolId, workspaceId, projectId } = payload;

    return this.http.get(
      `${environment.API_MICROSERVICE_URL.PROJECT}/tools/getWorkspaceIdVerified/${toolId}/${workspaceId}/${projectId}`
    );
  };

  /**
   * Retrieves the folder structure of a PMOne workspace by its ID.
   *
   * @param {string} workspaceId - The unique identifier of the PMOne workspace.
   * @returns An the folder structure of the specified workspace.
   */
  getPMOneWorkSpaceFileStructure = (workspaceId: string, projectId: string) =>
    this.http.get(
      `${environment.API_MICROSERVICE_URL.PROJECT}/tools/getPMOneWorkspaceFolderStructure/${workspaceId}/${projectId}`
    );

  /**
   * Retrieves the children (subfolders and sheets) of a specific folder in PMOne workspace.
   * Uses Smartsheet SDK 4.5.0 method: smartsheetInstance.folders.getFolderChildren()
   * This enables lazy loading - fetches children only when folder is expanded.
   * 
   * @param {string} folderId - The unique identifier of the folder.
   * @param {string} projectId - The project ID for tracking/logging (optional query param).
   * @returns An observable containing the folder's children (folders and sheets).
   */
  getPMOneFolderChildren = (folderId: string, projectId?: string) =>
    this.http.get(
      `${environment.API_MICROSERVICE_URL.PROJECT}/tools/getFolderChildren/${folderId}?childrenResourceTypes=folders,sheets`
    );

  /**
   * PreviewPmoneParameterPDF: Previews a PMOne parameter PDF by downloading it from the CMS.
   *
   * @param fileData - The data of the file to be downloaded.
   * @returns An observable or promise (depending on `baseService.downloadCMS`)
   *          that handles the download operation.
   */
  previewPmoneParameterPDF = (fileData: IFileData[]) => {
    const endpoint = `File/Download?containerName=${environment.CMS_CONTAINER_NAMES.DELIVERABLES}&type=Util`;

    return this.baseService.downloadCMS(endpoint, fileData);
  };

  /**
   * Verifies project name uniqueness for DT App tools.
   *
   * @param payload - An object containing the following properties:
   *   @param payload.projectId - The ID of the project.
   *   @param payload.projectName - The name of the project to verify.
   *   @param payload.toolName - The name of the tool.
   *   @param payload.clientName - The client name (optional, required for ALM with both validations).
   *
   * @returns An observable of the HTTP POST request response.
   */
  verifyProjectUniqueness = (payload: {
    projectId: number;
    projectName: string;
    toolName: string;
    clientName?: string;
  }) => {
    return this.baseService.post(
      '/tools/verify-dtapp-project-uniqueness',
      payload,
      environment.API_MICROSERVICE_URL?.PROJECT
    );
  };
}
